import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { apprenticeApi } from '../api/apprentices';
import { Apprentice } from '../types/apprentice.types';

export const ApprenticesList: React.FC = () => {
  const { user, userRole } = useAuth();
  const [apprentices, setApprentices] = useState<Apprentice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadApprentices = useCallback(async () => {
    if (!user?.tenant_id) return;

    try {
      setLoading(true);
      
      // For instructors, only load their caseload
      const instructorEmail = userRole === 'instructor' ? user.email : undefined;
      const response = await apprenticeApi.getAll(user.tenant_id, undefined, instructorEmail);
      
      setApprentices(response.data);
    } catch (error) {
      console.error('Failed to load apprentices:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id, user?.user_id, userRole]);

  useEffect(() => {
    loadApprentices();
  }, [loadApprentices]);

  const filteredApprentices = apprentices.filter((apprentice) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      apprentice.first_name.toLowerCase().includes(searchLower) ||
      apprentice.last_name.toLowerCase().includes(searchLower) ||
      apprentice.email.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading apprentices...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {userRole === 'instructor' ? 'My Caseload' : 'Apprentices'}
          </h1>
          <p className="text-gray-600">
            {userRole === 'instructor' 
              ? `Managing ${apprentices.length} apprentices`
              : `${apprentices.length} apprentices in the system`
            }
          </p>
        </div>
        {userRole === 'administrator' && (
          <Link
            to="/apprentices/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            Add Apprentice
          </Link>
        )}
      </div>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {filteredApprentices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">
            {searchTerm 
              ? 'No apprentices found matching your search.'
              : userRole === 'instructor'
              ? 'You have no apprentices assigned to you yet.'
              : 'No apprentices in the system yet.'
            }
          </p>
          {userRole === 'administrator' && !searchTerm && (
            <Link
              to="/apprentices/new"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Add First Apprentice
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  OTJ Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredApprentices.map((apprentice) => (
                <tr key={apprentice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {apprentice.first_name} {apprentice.last_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {apprentice.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {apprentice.employer_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(apprentice.otj_percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {Math.round(apprentice.otj_percentage)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        apprentice.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : apprentice.status === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {apprentice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/apprentices/${apprentice.id}`}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
